import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ElfsightWidget from '../components/about/ElfsightWidget';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { useScrollReveal } from '../hooks/use-scroll-reveal';
import heroImage from '../assets/hero-ai-transition.jpg';

const handleScheduleCall = () => {
  window.open('https://calendly.com/kentfrazier/30-minutes', '_blank');
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-4 mb-8">
    <span className="h-px w-12 bg-primary" aria-hidden="true" />
    <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
      {children}
    </p>
  </div>
);

const Hero = () => {
  const content = useScrollReveal<HTMLDivElement>();
  const tail = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative pt-56 pb-24 sm:pt-44 sm:pb-32 overflow-hidden bg-foreground">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt=""
          className="w-full h-full object-cover object-center opacity-35"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 bg-foreground/75" />
      <div
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{
          background:
            'radial-gradient(ellipse at 15% 80%, hsl(var(--primary) / 0.22) 0%, transparent 55%), radial-gradient(ellipse at 90% 10%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-24 md:gap-16 items-end">
          <div
            ref={content.ref}
            className={`md:col-span-7 transition-all duration-1000 ${
              content.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-primary" aria-hidden="true" />
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                AI Transformation
              </p>
            </div>
            <h1
              className="text-background text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] mb-10"
              style={{ textWrap: 'balance' }}
            >
              Only a person can be <span className="text-primary italic">trustworthy.</span>
            </h1>
            <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-light">
              AI is the easy part. Developing the human capacity to steer it is the scarce resource. We work with the leaders and companies deploying AI — from founder-led teams to the enterprise.
            </p>
          </div>

          <div
            ref={tail.ref}
            className={`md:col-span-5 md:pl-8 md:border-l md:border-background/15 transition-all duration-1000 delay-200 ${
              tail.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mb-5">
              For Companies Bringing AI Into Their Work
            </p>
            <p className="text-background/80 text-lg leading-relaxed font-light">
              Capital is flooding the technical layer. But most initiatives stall at adoption, where the human beings are. Without human judgment developed at scale, the failure rate stays high.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const TheMoment = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  return (
    <section className="bg-background py-24 sm:py-32">
      <div className="container-mobile max-w-5xl mx-auto">
        <div
          ref={heading.ref}
          className={`mb-14 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <Eyebrow>The Moment</Eyebrow>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Every company can now <span className="text-primary italic">buy intelligence.</span>
          </h2>
        </div>
        <div
          ref={body.ref}
          className={`space-y-7 text-lg sm:text-xl text-foreground/80 leading-relaxed font-light max-w-3xl transition-all duration-1000 delay-150 ${
            body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p>
            The same models and the same tools are available to anyone who can pay, and that is most companies now. The edge has moved from having intelligence to deploying it: where it belongs, how it enters, who carries the judgment around it.
          </p>
          <p>
            And most deployments stall in the same place. The workflows get mapped, the agents get built, and the initiative quietly dies at adoption, where the human beings are.
          </p>
        </div>
      </div>
    </section>
  );
};

const WhatWeSee = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-secondary py-24 sm:py-32 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 85% 15%, hsl(var(--primary) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div className="container-mobile relative z-10 max-w-5xl mx-auto">
        <div
          ref={heading.ref}
          className={`mb-14 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <Eyebrow>What We See</Eyebrow>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            AI arrives as a technology. It lands as an <span className="text-primary italic">identity event.</span>
          </h2>
        </div>
        <div
          ref={body.ref}
          className={`space-y-7 text-lg sm:text-xl text-foreground/80 leading-relaxed font-light max-w-3xl transition-all duration-1000 delay-150 ${
            body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p>
            The owner built the business on knowing everything, and now judgment is being redistributed. The person who holds twenty years of exceptions in her head is being asked to hand them to a system she suspects will replace her. Whether she trusts that process decides whether the system ever gets used.
          </p>
          <p>
            Deployment succeeds or stalls in moments like these. They never appear on the technical plan.
          </p>
        </div>
      </div>
    </section>
  );
};

const JudgmentBetween = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-24 sm:py-32 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, hsl(var(--primary) / 0.15) 0%, transparent 55%), radial-gradient(ellipse at 20% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div className="container-mobile relative z-10 max-w-5xl mx-auto">
        <div
          ref={heading.ref}
          className={`mb-14 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <Eyebrow>The Judgment Between</Eyebrow>
          <h2
            className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            A new judgment lives in the <span className="text-primary italic">space between.</span>
          </h2>
        </div>
        <div
          ref={body.ref}
          className={`space-y-7 text-lg sm:text-xl text-background/80 leading-relaxed font-light max-w-3xl transition-all duration-1000 delay-150 ${
            body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p>
            Every leader now makes a threefold call, over and over. What to delegate to people so they grow. What to delegate to machines so the work flows. What must remain human.
          </p>
          <p>
            That judgment is the scarce resource of this era. It can be developed. Developing it is our work.
          </p>
          <p className="pt-4">
            <Link
              to="/essays/the-space-between"
              onClick={() => window.scrollTo(0, 0)}
              className="group/link inline-flex items-center gap-3 text-background font-display text-lg hover:text-primary transition-colors"
            >
              <span className="border-b border-background/30 group-hover/link:border-primary pb-1 transition-colors">
                Read the essay: The Space Between
              </span>
              <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

const entries = [
  {
    label: 'The Dual Map Audit',
    body: 'One engagement produces two maps. Where intelligence belongs in your operation. Where your organization will quietly resist it. The second map predicts whether the investment survives.',
  },
  {
    label: 'Development Inside Deployment',
    body: 'Small, deliberate practices threaded into the rollout itself. Your people grow the judgment to run the business alongside the system, while the system goes live.',
  },
  {
    label: 'Built With Partners',
    body: 'We work the human arc alongside your technical team, or with builders we trust. The engagement leaves your company more capable of its next transition.',
  },
];

const HowWeWork = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  return (
    <section className="bg-background py-24 sm:py-32">
      <div className="container-mobile max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <Eyebrow>How We Work</Eyebrow>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Two maps, <span className="text-primary italic">drawn together.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {entries.map((e, i) => {
            const Card = () => {
              const { ref, visible } = useScrollReveal<HTMLDivElement>();
              return (
                <article
                  ref={ref}
                  style={{ transitionDelay: visible ? `${i * 120}ms` : '0ms' }}
                  className={`bg-card border border-border p-8 sm:p-10 flex flex-col transition-all duration-700 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <span className="h-px w-8 bg-primary" aria-hidden="true" />
                    <p className="text-primary font-semibold tracking-[0.2em] uppercase text-xs">
                      {e.label}
                    </p>
                  </div>
                  <p className="text-foreground/80 leading-relaxed text-base sm:text-lg font-light">
                    {e.body}
                  </p>
                </article>
              );
            };
            return <Card key={e.label} />;
          })}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-28 sm:py-40 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.18) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-3xl mx-auto text-center space-y-10 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <h2
          className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          style={{ textWrap: 'balance' }}
        >
          What <span className="text-primary italic">now?</span>
        </h2>
        <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-light max-w-xl mx-auto">
          One conversation is where it begins.
        </p>
        <div className="pt-2">
          <Button
            onClick={handleScheduleCall}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
          >
            Begin a Conversation
            <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const AITransition = () => {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AI Transformation — The Human Side of AI Deployment',
      provider: { '@type': 'Organization', name: 'ParadoxEdge', url: 'https://www.paradoxedge.com' },
      serviceType: ['AI Transformation', 'AI Adoption', 'Change Management', 'Leadership Development', 'Organizational Development'],
      description:
        'The human side of AI deployment. Dual-map audits, development inside deployment, and judgment for the space between people and machines.',
      url: 'https://www.paradoxedge.com/ai-transformation',
      areaServed: 'Worldwide',
      audience: { '@type': 'BusinessAudience', audienceType: 'Companies bringing AI into their work' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.paradoxedge.com/' },
        { '@type': 'ListItem', position: 2, name: 'AI Transformation', item: 'https://www.paradoxedge.com/ai-transformation' },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="AI Transformation | The Human Side of AI Deployment | ParadoxEdge"
        description="Most AI initiatives stall at adoption, where the human beings are. ParadoxEdge works the human side of AI deployment so the investment survives contact with your people."
        canonicalUrl="/ai-transformation"
        keywords="AI adoption, AI deployment, change management, human side of AI, AI transformation, leadership development"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <ElfsightWidget />
        <main>
          <Hero />
          <TheMoment />
          <WhatWeSee />
          <JudgmentBetween />
          <HowWeWork />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default AITransition;